import { Injectable, NgZone, inject, DOCUMENT } from '@angular/core';

import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

// Same entry assets as the deployed legacy indexTags/head.html and body.html.
export const COMMON_TRACKING_TAGS = [
  ...['240', '371', '428', '478', '621', '640', '672', '700', '757', '801', '830', '20201105']
    .map(id => 'assets/indexTags/head/' + id + '.html'),
  ...['428', '621', '640', '672', '700', '801', '830']
    .map(id => 'assets/indexTags/body/' + id + '.html'),
];

@Injectable({ providedIn: 'root' })
export class TrackingService {
  private doc = inject(DOCUMENT);
  private http = inject(HttpClient);
  private zone = inject(NgZone);
  private readonly injected = new Map<string, Promise<void>>();

  async initializeCommon(): Promise<void> {
    await Promise.allSettled(COMMON_TRACKING_TAGS.map(path => this.injectTag(path)));
  }

  injectTag(assetPath: string): Promise<void> {
    if (!assetPath) return Promise.resolve();
    const existing = this.injected.get(assetPath);
    if (existing) return existing;
    const pending = this.loadTag(assetPath).catch(() => {
      // A missing/failed optional asset may be retried on the next entry/completion.
      this.injected.delete(assetPath);
    });
    this.injected.set(assetPath, pending);
    return pending;
  }

  private async loadTag(assetPath: string): Promise<void> {
      const html = await firstValueFrom(this.http.get(assetPath, { responseType: 'text' }));
      if (this.isAppShellHtml(html)) throw new Error('Tracking asset returned the app shell');
      this.zone.runOutsideAngular(() => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        doc.querySelectorAll('script').forEach((orig) => {
          if (this.isAppBundleScript(orig.src)) return;
          const s = this.doc.createElement('script');
          for (const attr of ['type', 'crossorigin', 'integrity', 'referrerpolicy']) {
            if (orig.hasAttribute(attr)) s.setAttribute(attr, orig.getAttribute(attr)!);
          }
          if (orig.src) {
            s.src = orig.src;
            s.async = true;
          } else {
            s.textContent = orig.textContent;
          }
          this.doc.body.appendChild(s);
        });
        doc.querySelectorAll('noscript').forEach((orig) => {
          if (!orig.innerHTML.trim()) return;
          const ns = this.doc.createElement('noscript');
          ns.innerHTML = orig.innerHTML;
          this.doc.body.appendChild(ns);
        });
      });
  }

  private isAppShellHtml(html: string): boolean {
    return /<app-root[\s>]/i.test(html) || this.hasAppBundleScript(html);
  }

  private hasAppBundleScript(html: string): boolean {
    return /<script[^>]+src=["'][^"']*(?:polyfills|main|runtime)(?:\.[^"']*)?\.js[^"']*["']/i.test(html);
  }

  private isAppBundleScript(src: string): boolean {
    return /(?:^|\/)(?:polyfills|main|runtime)(?:\.[^/?#]+)?\.js(?:[?#].*)?$/i.test(src);
  }
}
